/**
 * CONFIGURACIÓN CENTRALIZADA DE RUTAS POR ROL
 * 
 * Este archivo centraliza todas las rutas y configuraciones relacionadas
 * con roles de usuario para mantener consistencia en toda la aplicación.
 * 
 * DIFERENCIAS CON src/routes.js:
 * - routes.js: Define rutas de React Router, componentes, sidebar, iconos
 * - roleRoutes.js: Solo lógica de roles, permisos y redirecciones
 * 
 * VENTAJAS:
 * - Elimina duplicación de código de roles en múltiples archivos
 * - Una sola fuente de verdad para configuración de roles
 * - Fácil mantenimiento y extensión de roles
 * - Separación de responsabilidades (React Router vs Lógica de Roles)
 * 
 * @author Diego Alejandro
 * @version 1.1.0 
 */

/**
 * Mapeo principal de roles a sus rutas de dashboard
 * Esta configuración se usa en:
 * - Login para redirección post-autenticación
 * - Componentes de protección de rutas
 * - Validaciones de acceso
 * 
 * NOTA: por ahora las demas apuntan a /dashboard hasta crear dashboards específicos
 * 
 * ACTUALIZADO: El rol 'evaluador' (Distribuidor Autorizado) ahora redirige a su dashboard específico
 */
export const ROLE_DASHBOARD_ROUTES = {
  'admin': '/dashboard',        //Aqui va la ruta del Dashboard de ADMIN
  'empresa': '/welcome-empresa',      //Aqui va la ruta del Dashboard de EMPRESA  
  'evaluador': '/distribuidor-autorizado/dashboard',    //Aqui va la ruta del Dashboard específico del DISTRIBUIDOR AUTORIZADO
  'candidato': '/candidate-test', // Dashboard de candidatos
};

/**
 * Información detallada de cada rol
 * Incluye nombres para mostrar, rutas permitidas, permisos, etc.
 * 
 * ACTUALIZADO: Configuración completa para el rol 'evaluador' (Distribuidor Autorizado)
 */
export const ROLE_CONFIG = {
  'admin': {
    displayName: 'Super Admin',
    dashboardRoute: ROLE_DASHBOARD_ROUTES.admin,
    allowedPaths: [
      '/dashboard', 
      '/gestion-general', 
      '/gestion-roles-permisos',
      '/gestion-permisos',
      '/gestion-usuarios', 
      '/gestion-inventario', 
      '/gestion-candidato',
      '/informe-test',
    ],
    permissions: ['all'],
    description: 'Acceso completo al sistema'
  },
  'empresa': {
  displayName: 'Empresa', 
  dashboardRoute: ROLE_DASHBOARD_ROUTES.empresa,
  allowedPaths: [
    // Dashboard de bienvenida
    '/welcome-empresa',
    
    // Gestión de Candidatos
    '/gestion-candidato',
    '/gestion-candidato/cargar-candidatos',
    '/gestion-candidato/asignar-licencias',
    '/gestion-candidato/informes',
    '/informe-test',
    
    // Gestión de Licencias (NUEVO)
    '/gestion-licencias',
    '/gestion-licencias/comprar',
    '/gestion-licencias/historial',
    
    // Historial de Compras (NUEVO)
    '/historial-compras',
    
    // Resumen General (NUEVO)
    '/resumen-general',
    
    // Soporte Técnico (NUEVO)
    '/soporte-tecnico',
    
    // Perfil de usuario
    '/profile',
    '/gestion-roles-permisos',
    '/gestion-permisos',
  ],
  permissions: ['manage_candidates', 'view_reports', 'buy_licenses', 'view_purchase_history', 'technical_support'],
  // buy_licenses (comprar licencias), view_purchase_history (ver historial de compras) y technical_support (soporte técnico)
  description: 'Gestión de candidatos, licencias y visualización de reportes'
  },
  
  'evaluador': {
    displayName: 'Distribuidor Autorizado',
    dashboardRoute: ROLE_DASHBOARD_ROUTES.evaluador, // Actualizado para usar el dashboard específico
    allowedPaths: [
      // Dashboard específico del Distribuidor Autorizado
      '/distribuidor-autorizado/dashboard',
      
      // Gestión de empresas
      '/distribuidor-autorizado/empresas/crear',
      '/distribuidor-autorizado/empresas/asignar',
      
      // Gestión de minoristas
      '/distribuidor-autorizado/minoristas/crear',
      '/distribuidor-autorizado/minoristas/asignar',
      
      // Compra de Licencias
      '/distribuidor-autorizado/compra-licencias',
      
      // Soporte Técnico
      '/distribuidor-autorizado/soporte',
      
      // Perfil (opcional, puedes quitarlo si no lo necesitan)
      '/profile',
    ],
    permissions: [
      'manage_companies', 
      'manage_retailers', 
      'buy_licenses', 
      'view_reports',
      'technical_support'
    ],
    description: 'Gestión de empresas, minoristas y compra de licencias'
  },

  'candidato': {
    displayName: 'Talento en Evaluación',
    dashboardRoute: ROLE_DASHBOARD_ROUTES.candidato,
    allowedPaths: [
      '/candidate-test',
    ],
    permissions: ['take_tests', 'view_own_results'],
    description: 'Realizar evaluaciones y ver resultados propios'
  }
};

/**
 * Obtiene la ruta de dashboard para un rol específico
 * @param {string} role - El rol del usuario
 * @returns {string} La ruta del dashboard o ruta por defecto
 */
export const getDashboardRoute = (role) => {
  return ROLE_DASHBOARD_ROUTES[role] || '/dashboard';
};

/**
 * Obtiene la configuración completa de un rol
 * @param {string} role - El rol del usuario
 * @returns {Object} Configuración del rol o configuración por defecto
 */
export const getRoleConfig = (role) => {
  return ROLE_CONFIG[role] || {
    displayName: role,
    dashboardRoute: '/dashboard',
    allowedPaths: ['/dashboard'],
    permissions: [],
    description: 'Rol no configurado'
  };
};

/**
 * Verifica si un usuario con cierto rol puede acceder a una ruta
 * @param {string} role - El rol del usuario
 * @param {string} path - La ruta a verificar
 * @returns {boolean} true si puede acceder, false si no
 */
export const canAccessPath = (role, path) => {
  const config = getRoleConfig(role);
  return config.allowedPaths.some(allowedPath => 
    path.startsWith(allowedPath)
  );
};

/**
 * Valida si un rol existe en el sistema
 * @param {string} role - El rol a validar
 * @returns {boolean} true si es válido, false si no
 */
export const isValidRole = (role) => {
  return Object.keys(ROLE_CONFIG).includes(role);
};

/**
 * Obtiene todos los roles disponibles para formularios/selects
 * @returns {Array} Array de objetos con información de roles
 */
export const getAllRoles = () => {
  return Object.keys(ROLE_CONFIG).map(role => ({
    value: role,
    label: ROLE_CONFIG[role].displayName,
    description: ROLE_CONFIG[role].description
  }));
};

/**
 * Obtiene la ruta de redirección correcta después del login
 * Considera la ruta de origen si es válida para el rol
 * 
 * ACTUALIZADO: Ahora redirige correctamente al dashboard específico del Distribuidor Autorizado
 * 
 * @param {string} role - El rol del usuario
 * @param {string} from - La ruta desde donde venía (opcional)
 * @returns {string} La ruta de redirección
 */
export const getRedirectAfterLogin = (role, from = null) => {
  // Si hay una ruta de origen y es válida para el rol, usar esa
  if (from && from !== '/' && from !== '/authentication/sign-in') {
    if (canAccessPath(role, from)) {
      return from;
    }
  }
  
  // Si no, redirigir al dashboard específico del rol
  return getDashboardRoute(role);
};

/**
 * Verifica si el usuario debe ser redirigido a su dashboard específico
 * 
 * ACTUALIZADO: Maneja correctamente la redirección del Distribuidor Autorizado
 * 
 * @param {string} role - El rol del usuario
 * @param {string} currentPath - La ruta actual
 * @returns {string|null} La ruta a redirigir o null si no necesita redirección
 */
export const shouldRedirectToDashboard = (role, currentPath) => {
  const config = getRoleConfig(role);
  
  // Si está en el dashboard genérico, redirigir a su dashboard específico
  if (currentPath === '/dashboard' && config.dashboardRoute !== '/dashboard') {
    return config.dashboardRoute;
  }
  
  // Si está tratando de acceder a una ruta no permitida
  if (!canAccessPath(role, currentPath)) {
    return config.dashboardRoute;
  }
  
  return null; // No necesita redirección
};
